import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Time } from 'lightweight-charts';
import { CandlestickData } from '@server/data-feed/schemas/output.schema';

export type CandlestickUpdateType = 'snapshot' | 'delta';

export type CandlestickUpdatePoint = Omit<CandlestickData, 'time'> & {
  time: Time;
};

export type CandlestickUpdate = {
  timeframeIndex: number;
  updateType: CandlestickUpdateType;
  data: CandlestickUpdatePoint[];
};

export type NewCandlestickEvent = {
  timeframeIndex: number;
  time: Time | null;
};

export type CandlestickUpdateStream = {
  getCandlestickUpdate$: () => Observable<CandlestickUpdate>;
  getNewCandlestick$: () => Observable<NewCandlestickEvent>;
  getCandlestickData$: (
    timeframeIndex: number
  ) => Observable<CandlestickUpdatePoint[]>;
  getLatestCandlesticks: (timeframeIndex: number) => CandlestickUpdatePoint[];
  getCandlestickSnapshots: (timeframeIndex: number) => CandlestickUpdate[];
  emitCandlestickUpdate: (update: CandlestickUpdate) => void;
  resetCandlestickSnapshots: () => void;
  dispose: () => void;
};

export const createCandlestickUpdateStream = (): CandlestickUpdateStream => {
  const candlestickUpdate$ = new Subject<CandlestickUpdate>();
  const newCandlestick$ = new Subject<NewCandlestickEvent>();
  // Shared per-timeframe full-candle state, so consumers don't keep duplicate candle caches.
  const candlestickDataByTimeframe = new Map<
    number,
    BehaviorSubject<CandlestickUpdatePoint[]>
  >();

  const getOrCreateCandlestickDataSubject = (timeframeIndex: number) => {
    let subject = candlestickDataByTimeframe.get(timeframeIndex);
    if (!subject) {
      subject = new BehaviorSubject<CandlestickUpdatePoint[]>([]);
      candlestickDataByTimeframe.set(timeframeIndex, subject);
    }

    return subject;
  };

  const getCandlestickUpdate$ = () => candlestickUpdate$.asObservable();
  const getNewCandlestick$ = () => newCandlestick$.asObservable();
  const getCandlestickData$ = (timeframeIndex: number) =>
    getOrCreateCandlestickDataSubject(timeframeIndex).asObservable();
  const getLatestCandlesticks = (timeframeIndex: number) =>
    getOrCreateCandlestickDataSubject(timeframeIndex).getValue();

  const getCandlestickSnapshots = (
    timeframeIndex: number
  ): CandlestickUpdate[] => {
    const data = getOrCreateCandlestickDataSubject(timeframeIndex).getValue();

    if (!data.length) return [];

    return [
      {
        timeframeIndex,
        updateType: 'snapshot',
        data
      }
    ];
  };

  const emitCandlestickUpdate = (update: CandlestickUpdate) => {
    const dataSubject = getOrCreateCandlestickDataSubject(
      update.timeframeIndex
    );
    const previousCandlesticks = dataSubject.getValue();
    const previousLastCandleTime =
      previousCandlesticks[previousCandlesticks.length - 1]?.time;

    if (update.updateType === 'snapshot') {
      dataSubject.next(update.data);
    } else {
      const mergedData = mergeCandlestickPoints(
        dataSubject.getValue(),
        update.data
      );
      dataSubject.next(mergedData);
    }

    const nextCandlesticks = dataSubject.getValue();
    const nextLastCandleTime =
      nextCandlesticks[nextCandlesticks.length - 1]?.time;
    if (
      nextLastCandleTime != null &&
      nextLastCandleTime !== previousLastCandleTime
    ) {
      newCandlestick$.next({
        timeframeIndex: update.timeframeIndex,
        time: nextLastCandleTime
      });
    } else if (nextLastCandleTime == null && previousLastCandleTime != null) {
      newCandlestick$.next({
        timeframeIndex: update.timeframeIndex,
        time: null
      });
    }

    candlestickUpdate$.next(update);
  };

  const resetCandlestickSnapshots = () => {
    const cachedTimeframes = new Set([...candlestickDataByTimeframe.keys()]);

    // Clear currently rendered candlestick state while waiting for the next snapshot.
    cachedTimeframes.forEach((timeframeIndex) => {
      const dataSubject = getOrCreateCandlestickDataSubject(timeframeIndex);
      const hadCandlesticks = dataSubject.getValue().length > 0;
      dataSubject.next([]);
      candlestickUpdate$.next({
        timeframeIndex,
        updateType: 'snapshot',
        data: []
      });
      if (hadCandlesticks) {
        newCandlestick$.next({
          timeframeIndex,
          time: null
        });
      }
    });
  };

  const dispose = () => {
    candlestickDataByTimeframe.forEach((subject) => {
      subject.complete();
    });
    candlestickDataByTimeframe.clear();
    candlestickUpdate$.complete();
    newCandlestick$.complete();
  };

  return {
    getCandlestickUpdate$,
    getNewCandlestick$,
    getCandlestickData$,
    getLatestCandlesticks,
    getCandlestickSnapshots,
    emitCandlestickUpdate,
    resetCandlestickSnapshots,
    dispose
  };
};

export const mergeCandlestickPoints = (
  existing: CandlestickUpdatePoint[],
  updates: CandlestickUpdatePoint[]
): CandlestickUpdatePoint[] => {
  const merged = [...existing];

  updates.forEach((update) => {
    const last = merged[merged.length - 1];
    if (last?.time === update.time) {
      merged[merged.length - 1] = update;
      return;
    }

    merged.push(update);
  });

  return merged;
};
